/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import { selectDraftEnvironmentId } from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  selectPlan,
  selectResourceGroupIdForPremiumPlan
} from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import {
  getTenant,
  selectAccountAuthorDomainName,
  selectInstanceId,
  selectInstanceRegion,
  selectRecentEditorDomainName
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { parse as parseCrn } from '@ibm-watson/shared/modules/ia/util/crn';
import type { RootState } from '@ibm-watson/shared/ts/state';
import { WebChatLayoutMode } from '../../../ia/components/preview/util';
import { isWxoEnabled, deployEnv } from '../../../ia/util/config';
import { WEBCHAT_CHANNEL_TYPE } from '../../util/channelTypes';
import { getRegionValue, getRegionValueForEnv } from '../../util/urls';
import { selectAllIntegrations, selectIntegrationById } from './selectors';

export const selectWebchatSettingsByIntegrationId = (
  state: RootState,
  integrationId: string
) => {
  const instance = selectInstanceId(state);
  const integration = selectIntegrationById(state, integrationId);

  const { instance: tenant } = getTenant(state);
  const { service_instance: serviceInstanceID = 'null-serviceInstanceId' } =
    parseCrn(tenant);

  if (_.isNil(instance) || _.isNil(integration)) {
    return null;
  }

  const plan = selectPlan(state);
  const region = selectInstanceRegion(state);
  const subscriptionID = selectResourceGroupIdForPremiumPlan(state);
  const regionValue = isWxoEnabled
    ? getRegionValueForEnv(region, deployEnv)
    : getRegionValue(region);

  return {
    integration,
    plan,
    subscriptionID,
    region: regionValue,
    serviceInstanceID
  };
};

export const selectWebChatIntegrationsByEnvironment = (
  state: RootState,
  environmentId: string
) =>
  selectAllIntegrations(state).filter(
    integration =>
      integration.type === WEBCHAT_CHANNEL_TYPE &&
      integration.environment_id === environmentId
  );

export const selectDraftWebChat = (state: RootState) => {
  const integrations = selectWebChatIntegrationsByEnvironment(
    state,
    selectDraftEnvironmentId(state)
  );
  if (integrations.length > 0) {
    return integrations[0];
  }
  return null;
};

export const selectDraftWebChatPreviewLayout = (
  state: RootState
): WebChatLayoutMode => {
  const integrations = selectWebChatIntegrationsByEnvironment(
    state,
    selectDraftEnvironmentId(state)
  );
  if (integrations?.length > 0) {
    return integrations[0].settings.preview?.layout.mode;
  }
  return null;
};

export const selectWebchatDefaultPlanSettings = (state: RootState) => ({
  accountOwnerDomain: selectAccountAuthorDomainName(state),
  plan: selectPlan(state),
  recent_editor_domain: selectRecentEditorDomainName(state)
});
